/**
 * Dev/test loop-engineering auth bypass — the frontend half of DEV_API_TOKEN.
 *
 * The dashboard normally logs in via interactive MSAL (Entra), which a headless
 * browser / automation can't complete. When a dev token is present we instead
 * short-circuit MSAL: the wrapped instance hands the dev token to every
 * `acquireTokenSilent` caller (so all API + WebSocket calls carry it), and the
 * app renders as if authenticated. The backend accepts the same token (see
 * services/auth.py DEV_API_TOKEN) and attributes it to a "dev-service" principal.
 *
 * Active ONLY when a dev token is configured, via either:
 *   - localStorage key `devApiToken`  (set at runtime in the browser console —
 *     no rebuild; ideal for driving the DEPLOYED app), or
 *   - VITE_DEV_API_TOKEN               (baked into a dev/test build).
 * A normal production build with neither set has this code inert.
 *
 * NEVER set VITE_DEV_API_TOKEN in a production build.
 */
import type { AccountInfo, AuthenticationResult, IPublicClientApplication } from "@azure/msal-browser";
import { apiScopes } from "./msalConfig";

const LS_KEY = "devApiToken";

export function getDevToken(): string {
  try {
    const fromLs = localStorage.getItem(LS_KEY);
    if (fromLs) return fromLs;
  } catch {
    /* localStorage may be unavailable (SSR/sandbox) — fall through to env */
  }
  return (import.meta.env.VITE_DEV_API_TOKEN as string | undefined) ?? "";
}

export function isDevAuth(): boolean {
  return getDevToken().length > 0;
}

// A minimal MSAL-shaped account so `useMsal().accounts[0]` and any name display
// keep working while signed in via the dev token.
const DEV_ACCOUNT: AccountInfo = {
  homeAccountId: "dev-service",
  environment: "dev.local",
  tenantId: "dev",
  username: "loop-engineer@dev.local",
  localAccountId: "dev-service",
  name: "loop-engineer (dev token)",
};

/** In dev mode, override the MSAL instance so token acquisition returns the dev
 *  token and account lookups return the synthetic dev account — covering every
 *  `instance.acquireTokenSilent(...)` call site without touching any of them. */
export function applyDevAuth(instance: IPublicClientApplication): void {
  const token = getDevToken();
  if (!token) return;

  const result = {
    accessToken: token,
    account: DEV_ACCOUNT,
    idToken: "",
    idTokenClaims: {},
    scopes: apiScopes,
    expiresOn: new Date(Date.now() + 60 * 60 * 1000),
    fromCache: false,
    tokenType: "Bearer",
    uniqueId: "dev-service",
    tenantId: "dev",
    authority: "",
    correlationId: "dev",
  } as unknown as AuthenticationResult;

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const inst = instance as any;
  inst.acquireTokenSilent = async () => result;
  inst.acquireTokenPopup = async () => result;
  inst.ssoSilent = async () => result;
  inst.getAllAccounts = () => [DEV_ACCOUNT];
  inst.getActiveAccount = () => DEV_ACCOUNT;
  inst.getAccountByHomeId = () => DEV_ACCOUNT;
  inst.getAccountByLocalId = () => DEV_ACCOUNT;
  inst.loginRedirect = async () => { /* already "signed in" via dev token */ };
  inst.loginPopup = async () => result;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  // eslint-disable-next-line no-console
  console.warn("[devAuth] MSAL bypassed — using DEV_API_TOKEN. Dev/test only.");
}
